import React, { useEffect, useState } from "react";
import Input from "../../../component/common/Input";
import { ReactComponent as TickIcon } from "../../../assets/tick.svg";
import Button from "../../../component/common/Button";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../../context/UserContext";
import httpServices from "../../../services/httpServices";
import CustomSelect from "../../../component/common/CustomSelect";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

const eventVenueDetailsSchema = Yup.object().shape({
  eventHostOnOwnVenue: Yup.boolean().required(
    "Event hosting status is required"
  ),
  eventType: Yup.string().required("Event type is required"),
  preferredArea: Yup.string()
    .min(2, "Preferred area must be at least 2 characters long")
    .required("Preferred area is required"),
  noOfPeoples: Yup.number()
    .min(1, "There must be at least 1 person")
    .required("Number of people is required")
    .typeError("Invalid number"),
});
const emptySchema = Yup.object().shape({}); // An empty schema for cases with no validation

const eventTypeOptions = [
  { label: "Corporate", value: "Corporate", color: "#8000ff" },
  { label: "Wedding", value: "Wedding", color: "#ff4c61" },
  { label: "Concert", value: "Concert", color: "#3b82a0" },
  { label: "Private Party", value: "Private Party", color: "#f1a839" },
];

const EventVenue = ({ eventId }) => {
  const navigate = useNavigate();
  const [isOwnVenue, setIsOwnVenue] = useState(true);
  const [allVenuesData, setAllVenuesData] = useState([]);
  const [selectedVenueId, setSelectedVenueId] = useState("");
  const [eventType, setEventType] = useState("");
  const { user } = useUser();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(isOwnVenue ? emptySchema : eventVenueDetailsSchema),
  });

  const getAllVenuesByVenderId = async (id) => {
    try {
      const { response, error } = await httpServices.get(
        `/venue/byVendorId/${id}`
      );

      if (error) {
        throw error;
      }
      if (response.status === 200) {
        const venues = response?.data?.data?.venues || [];
        setAllVenuesData(venues);
      }
    } catch (error) {
      console.log("Error", error);
    }
  };

  useEffect(() => {
    if (user?.userId) {
      getAllVenuesByVenderId(user.userId);
    }
  }, [user?.userId]);

  const venuesList = allVenuesData?.map((venue) => ({
    label: venue.venueName,
    value: venue.id,
  }));

  const onSubmit = async (data) => {
    if (isOwnVenue && selectedVenueId === "") {
      toast.error("Please select a venue");
      return;
    }
    const venueName = allVenuesData.find(
      (venue) => venue.id === selectedVenueId
    )?.venueName;
    const finalData = isOwnVenue
      ? {
          venueId: selectedVenueId,
          stepDone: 2,
          eventStatus: 1,
          eventVenueDetails: {
            eventVenueName: venueName,
            eventHostOnOwnVenue: true,
          },
        }
      : {
          eventVenueDetails: {
            eventType: data.eventType,
            preferredArea: data.preferredArea,
            noOfPeoples: data.noOfPeoples,
            stepDone: 2,
            eventStatus: 1,
            eventHostOnOwnVenue: false,
          },
        };
    console.log("finalData",eventId, finalData);
    try {
      const { response, error } = await httpServices.put(
        `/event/update/${eventId}`,

        {
          venueId: selectedVenueId,
          stepDone: 2,
          eventStatus: 1,
          userId: user.userId
        }
      );
      if (error) {
        throw error;
      }
      if (response.status === 200) {
        console.log("response", response);
        navigate(`/events/${response.data.data.id}`);
      }
    } catch (error) {
      console.log("error", error);
      toast.error("Something went wrong");
    }
  };
  const onError = (errors) => {
    console.log("Error:", errors);
  };

  const changeVenueHostOption = (value) => {
    setValue("eventHostOnOwnVenue", value);
    setIsOwnVenue(value);
  };

  return (
    <div className=" p-3 relative sm:m-4 sm:p-4 max-w-7xl bg-white rounded-[10px] min-h-[600px]">
      <h1 className="gradient-text font-semibold text-xl">Event Venue</h1>

      <div className="flex gap-[30px] ">
        <div className="flex space-x-2 mt-[20px] items-center">
          <div className="relative h-4 w-4">
            <input
              type="checkbox"
              className="absolute w-full h-full cursor-pointer rounded-md border-blue-color border-[1px] focus:ring-0 appearance-none peer"
              id="first"
              onChange={() => changeVenueHostOption(true)}
              checked={isOwnVenue}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2E3E8D] to-[#4DB1E1] rounded-md hidden peer-checked:block transition-opacity duration-200 pointer-events-none"></div>
            <TickIcon className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 hidden peer-checked:block transition-opacity duration-200 pointer-events-none" />
          </div>

          <label
            className="font-medium text-[rgba(6, 21, 43, 1)] cursor-pointer"
            htmlFor="first"
          >
            I want to host on my own venue
          </label>
        </div>
        <div className="flex space-x-2 mt-[20px] items-center">
          <div className="relative h-4 w-4">
            <input
              type="checkbox"
              className="absolute w-full h-full cursor-pointer rounded-md border-blue-color border-[1px] focus:ring-0 appearance-none peer"
              id="second"
              onChange={() => changeVenueHostOption(false)}
              checked={!isOwnVenue}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2E3E8D] to-[#4DB1E1] rounded-md hidden peer-checked:block transition-opacity duration-200 pointer-events-none"></div>
            <TickIcon className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 hidden peer-checked:block transition-opacity duration-200 pointer-events-none" />
          </div>

          <label
            className="font-medium text-[rgba(6, 21, 43, 1)] cursor-pointer"
            htmlFor="second"
          >
            I don’t have any venue
          </label>
        </div>
      </div>

      <form className="mt-[20px]" onSubmit={handleSubmit(onSubmit, onError)}>
        {isOwnVenue ? (
          <>
            <label
              className={`mb-2 text-sm lg:text-[16px] font-medium text-black block self-start `}
            >
              Your Venues*
            </label>
            <div className="flex items-center">
              <div className="w-full sm:w-[48%] lg:w-[25%]">
                <CustomSelect
                  options={venuesList}
                  onChange={(value) => {
                    setSelectedVenueId(value);
                  }}
                  value={selectedVenueId}
                />
                <p className="text-red-500  text-xs">
                  {selectedVenueId === "" &&
                    "Please select a venue from the list"}
                </p>
              </div>

              <span className="mx-[18px]">Or</span>

              <div className="w-1/2">
                <button
                  className="pl-3 py-3 bg-[#F5F6FA] w-full rounded-[10px] placeholder:text-500-gray text-sm text-center focus:outline-none appearance-none"
                  onClick={() => navigate("/create-venue")}
                >
                  + Add your Venue
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="w-1/2 space-y-[20px]">
              <div className="w-full ">
                <label className="mb-2 text-sm lg:text-[16px] font-medium text-black block">
                  What is your event type*
                </label>
                <CustomSelect
                  options={eventTypeOptions}
                  onChange={(value) => {
                    setEventType(value);
                    setValue("eventType", value);
                  }}
                  value={eventType}
                />
                <p className="text-red-500  text-xs">
                  {eventType === "" && errors?.eventType?.message}
                </p>
              </div>
              <div>
                <Input
                  label={"what is your preferred area?*"}
                  placeholder={"Enter preferred area"}
                  inputClasses="bg-[#F5F6FA] pl-3 rounded-[10px]"
                  lableClasses="text-sm lg:text-[16px]"
                  {...register("preferredArea")}
                />
                {errors.preferredArea && (
                  <p className="text-red-500 text-xs">
                    {errors.preferredArea.message}
                  </p>
                )}
              </div>
              <div>
                <Input
                  label={"How many people are going to attend?*"}
                  placeholder={"No of people"}
                  type="number"
                  inputClasses="bg-[#F5F6FA] pl-3 rounded-[10px]"
                  lableClasses="text-sm lg:text-[16px]"
                  {...register("noOfPeoples")}
                />
                {errors.noOfPeoples && (
                  <p className="text-red-500 text-xs">
                    {errors.noOfPeoples.message}
                  </p>
                )}
              </div>
            </div>
          </>
        )}
        <div className="flex justify-center absolute bottom-5 left-1/2 translate-x-[-50%]">
          <Button
            content={"Save & Proceed"}
            classes={"py-[9px] px-[62px]  inline-block btn-gradient"}
            type="submit"
          />
        </div>
      </form>
    </div>
  );
};

export default EventVenue;
